import React, { useRef } from "react";
import { Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FormErrorMessage } from "../../components";
import { toast } from "react-toastify";
// If your ImageSupportedType are MIME strings (recommended), keep them.
// Otherwise we’ll validate by MIME OR extension.
import { ImageSupportedType } from "../../configs/appConstants";
import { useFunctionalityUserProfile } from "../../pages/hooks";

type Props = {
  show: boolean;
  handleShow: (value: boolean) => void;
  onUploadComplete: () => void;
  useId: string; // keeping your prop name
};

// IMPORTANT: file inputs provide a FileList
type TypeProfilePicture = {
  photo: FileList;
};

const MAX_SIZE = 2 * 1024 * 1024; // 2MB

export function ModalChangeProfilePhoto(props: Props): JSX.Element {
  const { show, handleShow, onUploadComplete, useId } = props;
  const { onUploadPhoto } = useFunctionalityUserProfile();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset,
  } = useForm<TypeProfilePicture>();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const files = watch("photo");
  const currentFile = files?.[0];
  const selectedName = currentFile?.name ?? "";

  // MIME + extension validation (covers SVGs - no longer supported - and edge cases)
  const isTypeAllowed = (file: File) => {
    const mimeOk =
      /^(image\/(png|jpeg|gif))$/i.test(file.type) ||
      // If your constants are MIME strings, include them here too:
      file.type === (ImageSupportedType?.PNG as any) ||
      file.type === (ImageSupportedType?.JPEG as any) ||
      file.type === (ImageSupportedType?.JPG as any) ||
      file.type === (ImageSupportedType?.GIF as any);

    const extOk = /\.(png|jpe?g|gif)$/i.test(file.name);
    return mimeOk || extOk;
  };

  const onSubmit = async (data: TypeProfilePicture) => {
    const file = data.photo?.[0];
    if (!file) {
      toast.error("Please select an image");
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error("Maximum image size exceeded (2MB)");
      return;
    }
    if (!isTypeAllowed(file)) {
      toast.error("Only accepted file types: .jpg, .jpeg, .png, .gif");
      return;
    }

    try {
      // ✅ Preferred (clean) path: pass File and let the API build FormData.
      //     Uncomment this line IF your onUploadPhoto signature is (file: File, userId: string)
      // await onUploadPhoto(file, useId);

      // 🔁 Back-compat path: if your onUploadPhoto expects (userId: string, form: FormData)
      // const fd = new FormData();
      // fd.append("file", file, file.name);
      await onUploadPhoto(useId, file);

      toast.success("Uploaded successfully");
      onUploadComplete();
      // full reset
      reset();
      if (fileInputRef.current) fileInputRef.current.value = "";
      handleShow(false);
    } catch (e) {
      console.error(e);
      toast.error("Upload failed");
    }
  };

  const openPicker = () => fileInputRef.current?.click();

  // const onHideAndReset = () => {
  //   handleShow(false);
  //   reset();
  //   if (fileInputRef.current) fileInputRef.current.value = "";
  // };

  const onHideAndReset = () => { handleShow(false); reset(); };

  return (
    <Modal show={show} onHide={onHideAndReset} centered>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>Upload Profile Picture</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <label className="position-relative required">Upload File</label>
                <small className="d-block text-muted">
                  Image size must not exceed 2MB
                </small>

                <div className="input-group">
                  {/* Readonly display of the chosen filename */}
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Choose a File"
                    aria-label="Upload file"
                    value={selectedName}
                    readOnly
                  />

                  {/* Actual file input (hidden) */}
                  <input
                    type="file"
                    id="profile-photo-file"
                    accept=".png,.jpg,.jpeg,.gif,image/*"
                    className="d-none"
                    {...register("photo", {
                      required: "Image is required",
                      validate: {
                        size: (fl) => !fl?.[0] || fl[0].size <= MAX_SIZE || "Image size must not exceed 2MB",
                        type: (fl) => !fl?.[0] || isTypeAllowed(fl[0]) || "Only accepted file types: .jpg, .jpeg, .png, .gif",
                      },
                    })}
                  />

                  <div className="input-group-append">
                    {/* <button
                      className="btn btn-primary"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        openPicker();
                      }}
                    >
                      <FontAwesomeIcon icon={faCamera} className="mr-2" />
                      <label className="mb-0" htmlFor="profile-photo-file">
                        Select a photo
                      </label>
                    </button> */}
                    <button type="button" className="btn btn-primary">
                      <label className="mb-0" htmlFor="profile-photo-file">Select a photo</label>
                    </button>
                  </div>
                </div>

                {/* Validation errors (RHF-driven) */}
                {errors.photo?.message && (
                  <FormErrorMessage message={String(errors.photo.message)} />
                )}
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-link text-site text-uppercase"
            onClick={onHideAndReset}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary text-uppercase upload-button"
            disabled={!currentFile || isSubmitting}
          >
            {isSubmitting ? "Uploading..." : "Upload"}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
